/* ==========================================================================
   MIDNIGHT SLICE — shared section behaviour
   Reveal-on-scroll, accordions, gallery slider, inline UGC clips, sticky bar.
   Gallery / quantity / variant handling stays with Impulse's theme.js.
   Re-runs on shopify:section:load so the theme editor stays live.
   ========================================================================== */
(function () {
  'use strict';

  var supportsIO = 'IntersectionObserver' in window;
  // One flag per feature. A single shared flag silently broke the carousel:
  // .ms-ugc__stage is also a .ms-reveal element, so initReveal marked it
  // first and initCarousel then treated it as already wired and bailed out.
  var BOUND = {
    reveal: 'msReveal',
    accordion: 'msAccordion',
    carousel: 'msCarousel',
    more: 'msMore',
    sticky: 'msSticky'
  };

  // Unlocks the reveal animation in ms-core.css. Done first so a later error
  // in this file cannot leave sections stuck at opacity 0.
  document.documentElement.classList.add('ms-js');

  /* ---------- reveal on scroll ---------- */
  function initReveal(scope) {
    var items = scope.querySelectorAll('.ms-reveal');
    if (!items.length) return;

    if (!supportsIO) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach(function (el) {
      if (el.dataset[BOUND.reveal]) return;
      el.dataset[BOUND.reveal] = '1';
      observer.observe(el);
    });
  }

  /* ---------- accordions ---------- */
  function initAccordions(scope) {
    scope.querySelectorAll('.ms-accbtn').forEach(function (btn) {
      if (btn.dataset[BOUND.accordion]) return;
      btn.dataset[BOUND.accordion] = '1';
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        // The panel is not adjacent to the button (the button is inside an
        // <h2>), so the open state has to live on the .ms-acc wrapper.
        var acc = btn.closest('.ms-acc');
        if (acc) acc.classList.toggle('is-open', !open);
      });
    });
  }

  /* ---------- collapsed product description ----------
     The button only appears in the markup when the description block is set
     to collapse, so there is nothing to do unless one is present. If the text
     is short enough to fit uncollapsed there is nothing to expand either, so
     the toggle removes itself rather than sitting there doing nothing. */
  function initReadMore(scope) {
    scope.querySelectorAll('[data-ms-more]').forEach(function (btn) {
      if (btn.dataset[BOUND.more]) return;
      btn.dataset[BOUND.more] = '1';

      var desc = document.getElementById(btn.getAttribute('aria-controls'));
      if (!desc) return;

      if (desc.scrollHeight <= desc.clientHeight + 1) {
        desc.classList.remove('is-clamped');
        btn.hidden = true;
        return;
      }

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        desc.classList.toggle('is-clamped', open);
        btn.textContent = open
          ? (btn.dataset.moreText || 'Read more')
          : (btn.dataset.lessText || 'Read less');
      });
    });
  }

  /* ---------- UGC carousel ---------- */
  function initCarousel(scope) {
    scope.querySelectorAll('.ms-ugc__stage').forEach(function (stage) {
      if (stage.dataset[BOUND.carousel]) return;
      stage.dataset[BOUND.carousel] = '1';

      var track = stage.querySelector('.ms-ugc__track');
      if (!track) return;

      var rail = createRail(stage, track);
      initDots(stage, rail, track);

      // The slider is set up first and independently: a problem with a video
      // must not be able to stop the rail from sliding.
      initAutoSlide(stage, rail);
      // Last, so its pointerdown handler runs after the autoplay's — the drag
      // needs to be the one that decides what snapping does while it lasts.
      initDrag(track, rail);
      try { initUgcVideos(stage); } catch (e) { /* clips are decorative */ }
    });
  }

  /* ---------- drag the rail ----------
     A mouse cannot drag an overflow container — that is scrollbar or wheel
     only — and this rail hides its scrollbar. So on a desktop there was no
     way to move it by hand at all, and the dots were the only control that
     did anything. Touch is left to the browser, whose native scrolling and
     momentum are better than anything reimplemented here. */
  function initDrag(track, rail) {
    var dragging = false;
    var startX = 0;
    var startLeft = 0;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      dragging = true;
      startX = e.clientX;
      startLeft = track.scrollLeft;
      rail.stop();
      track.style.scrollSnapType = 'none';
      track.classList.add('is-dragging');
      if (track.setPointerCapture) {
        try { track.setPointerCapture(e.pointerId); } catch (err) { /* not fatal */ }
      }
    });

    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      e.preventDefault();
      track.scrollLeft = startLeft - (e.clientX - startX);
    });

    function end() {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      // Handing snapping back settles the rail on the nearest card by itself.
      track.style.scrollSnapType = '';
    }
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('pointerleave', end);
  }

  /* ---------- the rail ----------
     One place that knows how wide a card is, how to glide to a position and
     how to wrap. The arrows and the autoplay both drive it, so a click and a
     tick move the rail in exactly the same way.

     Every move lands on a whole card. The rail used to scroll by a measured
     offset with behavior:'smooth', which drifted off the grid and left cards
     sliced at both edges. */
  function createRail(stage, track) {
    var originals = Array.prototype.slice.call(track.querySelectorAll('.ms-ugc__card'));

    function gap() {
      var s = getComputedStyle(track);
      return parseFloat(s.columnGap || s.gap) || 14;
    }
    function cardStep() {
      if (!originals.length) return 0;
      return originals[0].getBoundingClientRect().width + gap();
    }
    // one loop is the original run, first card to where the first clone starts
    function loopWidth() {
      if (originals.length < 2) return 0;
      var last = originals[originals.length - 1];
      return (last.offsetLeft + last.getBoundingClientRect().width + gap()) - originals[0].offsetLeft;
    }

    // Enough cloned cards to cover the rail, so stepping past the last one
    // lands on an identical card instead of stopping dead or rewinding. Only
    // as many as the rail can show — each clone is another video decoding.
    var cloned = false;
    function ensureClones() {
      if (cloned || originals.length < 2) return;
      var step = cardStep();
      if (!step) return;
      cloned = true;
      var need = Math.max(track.clientWidth, 320) + step;
      for (var i = 0, filled = 0; filled < need && i < originals.length * 3; i++) {
        var c = originals[i % originals.length].cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.dataset.msClone = '1';
        track.appendChild(c);
        filled += step;
      }
    }

    var anim = null;

    function settle() {
      // Wrapping happens here, on a card boundary, where the card under the
      // edge and the card one loop back are the same frame — so it is
      // invisible. Snap goes back on once we have landed; we are exactly on a
      // boundary, so it has nothing left to correct.
      var w = loopWidth();
      if (w > 0 && track.scrollLeft >= w - 1) track.scrollLeft = track.scrollLeft - w;
      track.style.scrollSnapType = '';
    }

    function glide(to, ms) {
      if (anim) { window.cancelAnimationFrame(anim); anim = null; }
      var from = track.scrollLeft;
      var dist = to - from;
      if (Math.abs(dist) < 1) { settle(); return; }

      // ms of 0 is the reduced-motion path: still move, just do not animate.
      if (!ms || ms < 16) { track.scrollLeft = to; settle(); return; }

      // Scroll snapping re-snaps after every scroll write, so left on it would
      // fight this animation frame by frame — that was the stutter.
      track.style.scrollSnapType = 'none';

      var t0 = 0;
      function frame(ts) {
        if (!t0) t0 = ts;
        var p = Math.min((ts - t0) / ms, 1);
        var e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2; // easeInOutCubic
        track.scrollLeft = from + dist * e;
        if (p < 1) anim = window.requestAnimationFrame(frame);
        else { anim = null; settle(); }
      }
      anim = window.requestAnimationFrame(frame);
    }

    return {
      canLoop: function () { return originals.length >= 2; },
      // Drop whatever the rail was doing and hand it back to the reader. A
      // glide writes scrollLeft every frame, so without this a swipe that
      // landed mid-animation was overwritten frame by frame and the rail
      // fought the finger.
      stop: function () {
        if (anim) { window.cancelAnimationFrame(anim); anim = null; }
        track.style.scrollSnapType = '';
      },
      advance: function (dir, ms) {
        ensureClones();
        var step = cardStep();
        if (!step) return;

        // Round to the grid before stepping, so a half-finished swipe cannot
        // leave every later move off by the same fraction.
        var target = Math.round(track.scrollLeft / step + dir) * step;

        // Going back from the first card: hop one loop forward first. The
        // content is identical, so the hop is invisible, and there is now a
        // card to the left to glide to.
        if (target < 0) {
          var w = loopWidth();
          if (w <= 0) return;
          track.scrollLeft = track.scrollLeft + w;
          target = Math.round(track.scrollLeft / step + dir) * step;
        }
        glide(target, ms || 620);
      },
      count: function () { return originals.length; },
      // Which original card is at the left edge. Modulo the count, so a
      // position out among the clones still reports the clip it is showing.
      index: function () {
        var step = cardStep();
        var n = originals.length;
        if (!step || !n) return 0;
        return ((Math.round(track.scrollLeft / step) % n) + n) % n;
      },
      goTo: function (i) {
        ensureClones();
        var step = cardStep();
        if (!step) return;
        glide(i * step, 620);
      }
    };
  }

  /* ---------- dots ----------
     Built here rather than in Liquid because only this side knows how many
     cards there are once the clones are in; a dot rendered per block could
     end up pointing at a clone. One dot per real clip, always. */
  function initDots(stage, rail, track) {
    var host = stage.querySelector('.ms-ugc__dots');
    if (!host || rail.count() < 2) return;

    var dots = [];
    for (var i = 0; i < rail.count(); i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ms-ugc__dot';
      dot.setAttribute('aria-label', 'Show clip ' + (i + 1) + ' of ' + rail.count());
      dot.addEventListener('click', (function (n) {
        return function () { rail.goTo(n); };
      })(i));
      host.appendChild(dot);
      dots.push(dot);
    }

    var queued = false;
    function paint() {
      queued = false;
      var current = rail.index();
      for (var i = 0; i < dots.length; i++) {
        var on = i === current;
        dots[i].classList.toggle('is-on', on);
        dots[i].setAttribute('aria-current', on ? 'true' : 'false');
      }
    }

    // Scroll fires far more often than the dots need repainting, and it fires
    // during every frame of a glide.
    track.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(paint);
    }, { passive: true });

    paint();
  }

  /* ---------- auto-advancing rail ----------
     Ticks the rail on one card at a time and lets createRail do the moving,
     so an automatic advance and an arrow click are the same motion.

     What was wrong before: it called scrollTo with behavior:'smooth' against a
     scroll-snap-type: x mandatory track, so the browser re-snapped in the
     middle of its own animation — that was the stutter. And on reaching the
     end it threw the whole track back to the start in one jump. The rail now
     steps onto cloned cards and wraps at a boundary, so there is no rewind.

     A reader's swipe or arrow click restarts the clock rather than being
     followed a moment later by an automatic move they did not ask for. */
  function initAutoSlide(stage, rail) {
    if (stage.dataset.msAutoplay !== 'true') return;
    if (!rail.canLoop()) return;

    var delay = Math.max(parseFloat(stage.dataset.msSpeed) || 2, 1) * 1000;
    // The glide has to finish inside the gap between ticks, or a short
    // interval starts the next move on top of the one still running and the
    // rail never comes to rest on a card. At the 2s default this is 620ms.
    var glideMs = Math.min(620, Math.round(delay * 0.55));

    // Reduced motion silences the animation, not the feature: the rail still
    // advances every tick, it just cuts rather than glides. Returning here
    // instead — which is what this did — left the carousel dead for anyone
    // with system animations off, and looked like autoplay was broken.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) glideMs = 0;
    var timer = null;
    var paused = false;
    var onScreen = true;

    function sync() {
      var run = !paused && onScreen;
      if (run && !timer) timer = window.setInterval(function () { rail.advance(1, glideMs); }, delay);
      if (!run && timer) { window.clearInterval(timer); timer = null; }
    }

    function restart() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
      sync();
    }

    /* ---------- the reader always wins ----------
       A touch, a drag or a wheel stops the current glide outright and holds
       the rail until they have finished, then gives it a moment before taking
       over again. Previously these only reset the interval, so an automatic
       glide carried on writing scrollLeft underneath a finger that was trying
       to drag the rail somewhere else. */
    var resume = null;
    function handOver() {
      rail.stop();
      if (resume) { window.clearTimeout(resume); resume = null; }
      paused = true;
      sync();
    }
    function handBack(ms) {
      if (resume) window.clearTimeout(resume);
      resume = window.setTimeout(function () {
        resume = null;
        paused = false;
        sync();
      }, ms);
    }

    var track = stage.querySelector('.ms-ugc__track');
    if (track) {
      track.addEventListener('pointerdown', handOver, { passive: true });
      track.addEventListener('touchstart', handOver, { passive: true });
      track.addEventListener('pointerup', function () { handBack(2500); }, { passive: true });
      track.addEventListener('pointercancel', function () { handBack(2500); }, { passive: true });
      track.addEventListener('touchend', function () { handBack(2500); }, { passive: true });
      track.addEventListener('touchcancel', function () { handBack(2500); }, { passive: true });
      track.addEventListener('wheel', function () {
        handOver();
        handBack(2000);
      }, { passive: true });
    }
    stage.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.ms-ugc__dot')) restart();
    });

    // Hover only pauses where hover actually exists; on touch a tap would
    // otherwise pause the rail permanently, since no leave event follows.
    // No pause on hover. It is the conventional thing to do, but it meant the
    // rail stood still for anyone who happened to have the pointer over the
    // section, which reads as autoplay not working at all. Touching, dragging
    // or wheeling still hands the rail over — that is the case that matters,
    // because those are the ones that would fight the reader.

    if (supportsIO) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.05 }).observe(stage);
    }

    sync();
  }

  /* ---------- UGC clips ----------
     Clips play inline, always muted and looping — no lightbox and no controls.
     autoplay is on the element itself, so this only pauses everything when the
     section leaves the screen and starts it again on the way back. */
  function initUgcVideos(stage) {
    // video_tag builds the element, so match on the tag rather than a data
    // attribute we would have to inject into it.
    var videos = Array.prototype.slice.call(stage.querySelectorAll('.ms-ugc__card video'));
    if (!videos.length) return;

    videos.forEach(function (v) {
      // belt and braces: the attributes are already on the element, but a
      // theme script rewriting markup could drop them
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
    });

    function play(video) {
      var started = video.play();
      if (started && started.catch) started.catch(function () {});
    }

    // Every clip runs together, including the ones scrolled off the side of the
    // rail, so nothing has to "start" as it slides in. Playback is tied to the
    // section rather than the individual card: all clips run while the section
    // is on screen and all stop once it isn't, so an off-screen section costs
    // nothing.
    if (supportsIO) {
      new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) videos.forEach(play);
        else videos.forEach(function (v) { v.pause(); });
      }, { threshold: 0.1 }).observe(stage);
    } else {
      videos.forEach(play);
    }
  }

  /* ---------- sticky add-to-cart bar ---------- */
  function initSticky(scope) {
    var bar = scope.querySelector('.ms-sticky');
    if (!bar || bar.dataset[BOUND.sticky]) return;
    bar.dataset[BOUND.sticky] = '1';

    // The real add-to-cart button, rendered by the theme's product form.
    var anchor = document.querySelector(
      '.product-single__meta [name="add"], .product-single__meta .add-to-cart'
    );
    if (!anchor) return;

    var btn = bar.querySelector('[data-ms-sticky-add]');
    var priceEl = bar.querySelector('[data-ms-sticky-price]');

    /* ---------- keep the bar honest ----------
       The bar is rendered from whichever variant was current at page load and
       never heard about it changing, which broke it two ways: the price stayed
       on the first variant for the life of the page, and landing on a sold-out
       variant left the button disabled even after the shopper picked one that
       was in stock — a dead Add to cart with nothing to explain it.

       Mirroring the real button rather than tracking variants ourselves keeps
       one source of truth. */
    function sync() {
      if (!btn) return;
      btn.disabled = !!anchor.disabled;
      btn.textContent = anchor.disabled
        ? (btn.dataset.soldOutText || btn.textContent)
        : (btn.dataset.defaultText || btn.textContent);
    }

    // The theme dispatches variant:change ahead of its own DOM work, so
    // reading the button in the same tick gets the previous variant's state.
    function syncFromVariant(e) {
      var variant = e && e.detail && e.detail.variant;
      if (variant && priceEl && window.theme && theme.Currency && theme.Currency.formatMoney) {
        priceEl.textContent = theme.Currency.formatMoney(variant.price, theme.settings && theme.settings.moneyFormat);
      }
      window.setTimeout(sync, 0);
    }

    sync();
    document.addEventListener('variant:change', syncFromVariant);

    if (supportsIO) {
      new IntersectionObserver(function (entries) {
        var e = entries[0];
        var show = !e.isIntersecting && e.boundingClientRect.top < 0;
        bar.classList.toggle('is-on', show);
        // Reserves foot room so the bar never sits on top of the footer.
        document.body.classList.toggle('ms-sticky-on', show);
      }, { threshold: 0 }).observe(anchor);
    }

    // Click the real button straight away. The old version scrolled to the
    // form and clicked 450ms later, which read as a broken button on every
    // click and did nothing at all when the scroll was interrupted.
    if (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (anchor.disabled) return;
        anchor.click();
      });
    }
  }

  function initAll(scope) {
    var root = scope || document;
    initReveal(root);
    initAccordions(root);
    initReadMore(root);
    initCarousel(root);
    initSticky(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor: re-init when a section is added or re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    initAll(document);
  });
})();
